/**
 * Video acceptance gate + metadata decode (spec 0020 Slice 2). Client-side pre-check ONLY — the real
 * upload endpoint (`POST /api/assets/videos`: auth + store-scope + rate-limit + server re-sniff + streamed
 * 30 MB cap) is the authoritative gate (see `uploadAsset.ts`). Here we validate MIME + size + magic bytes
 * (so a `.exe` renamed `.mp4` is rejected cheaply), then read width/height/duration from a `<video>` element
 * (NOT `new Image()`, which cannot decode video) for the CLS box + a real timeline.
 */
import { bt } from '../../i18n';

export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm'] as const;
export const MAX_VIDEO_BYTES = 30 * 1024 * 1024; // 30 MB — matches the backend VideoUploadService cap
export const WARN_VIDEO_BYTES = 10 * 1024 * 1024; // soft UX warning above ~10 MB (keep clips light for PageSpeed)

export interface VideoMeta {
  width: number;
  height: number;
  duration: number;
  /** The fixed MIME to store in VideoSource.type. */
  mime: 'video/mp4' | 'video/webm';
  /**
   * A `blob:` URL for the file the seller just picked, for INSTANT local preview.
   *
   * We already create one to decode the metadata; it used to be revoked immediately and thrown away, so the
   * inspector previewed `sources[0].url` — the REMOTE delivery URL — and pulled the very bytes that were
   * sitting in memory back down over the network (from Frankfurt, on a Moroccan uplink). Handing the blob
   * URL to the caller makes the preview appear the instant the upload resolves, at zero bytes.
   *
   * OWNERSHIP: the caller now owns this URL and MUST `URL.revokeObjectURL` it when it is replaced or the
   * component unmounts, otherwise the file stays pinned in memory for the life of the tab.
   */
  previewUrl: string;
  /**
   * A still frame grabbed from the clip IN THE BROWSER, before anything is uploaded.
   *
   * Without it a `<video>` has nothing to paint until enough of the file has arrived — which is why the
   * inspector (and a storefront hero) sat on an empty box "loading forever". With it, every surface paints
   * a real image instantly and the clip itself can stay `preload="none"` — zero bytes until someone
   * actually presses play.
   *
   * `undefined` when the browser could not decode a frame (exotic codec, seek refused). Always best-effort:
   * a failed capture must never block the upload.
   */
  poster?: Blob;
}

/** Longest we let the frame grab run before giving up and uploading without a poster. */
const POSTER_TIMEOUT_MS = 4000;
/** Posters are decoration, not content — cap the long edge so a 4K clip doesn't yield a 4K still. */
const POSTER_MAX_EDGE = 1280;

/** Draw the video's current frame to a canvas and encode it as WebP. Null if the frame isn't drawable. */
async function captureFrame(v: HTMLVideoElement): Promise<Blob | null> {
  const w = v.videoWidth;
  const h = v.videoHeight;
  if (!w || !h) return null;
  const scale = Math.min(1, POSTER_MAX_EDGE / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    // Same-origin blob: URL → the canvas is never tainted, so toBlob is allowed.
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
  } catch {
    return null;
  }
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/webp', 0.75));
}

/**
 * ISO-BMFF brands that are NOT browser-playable video. MUST mirror `VideoType.NON_VIDEO_BRANDS` on the
 * backend: when the two ends disagreed, the browser accepted a file that the server then rejected — so the
 * seller waited out an entire upload just to be told "use MP4 or WebM" about their MP4.
 */
const NON_VIDEO_BRANDS = new Set([
  'avif',
  'avis',
  'heic',
  'heix',
  'mif1',
  'msf1', // images
  'M4A ',
  'M4B ',
  'M4P ', // audio-only
  'qt  ', // QuickTime / MOV — plays inconsistently across browsers
]);

/** Sniff the leading magic bytes to confirm the file really is the video type it claims. */
async function sniffVideoMagic(file: File): Promise<'video/mp4' | 'video/webm' | null> {
  const buf = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  // WebM / Matroska: EBML header magic 1A 45 DF A3.
  if (buf[0] === 0x1a && buf[1] === 0x45 && buf[2] === 0xdf && buf[3] === 0xa3) return 'video/webm';
  // MP4 / ISO-BMFF: bytes 4..7 = "ftyp" (0x66 0x74 0x79 0x70), bytes 8..11 = the major brand.
  if (buf[4] === 0x66 && buf[5] === 0x74 && buf[6] === 0x79 && buf[7] === 0x70) {
    const brand = String.fromCharCode(buf[8]!, buf[9]!, buf[10]!, buf[11]!);
    return NON_VIDEO_BRANDS.has(brand) ? null : 'video/mp4';
  }
  return null;
}

/**
 * Validate + decode. Throws a user-facing message on rejection; returns the decoded metadata on success.
 * `mime` comes from the magic-byte sniff (not the forgeable `file.type`).
 */
export async function acceptVideoFile(file: File): Promise<VideoMeta> {
  if (!(ACCEPTED_VIDEO_TYPES as readonly string[]).includes(file.type)) {
    throw new Error(bt('chrome.media.video_type', 'Unsupported video type. Use MP4 or WebM.'));
  }
  if (file.size > MAX_VIDEO_BYTES) {
    throw new Error(
      bt(
        'chrome.media.video_too_large',
        'That video is too large (max 30 MB). Compress it or trim it shorter.',
      ),
    );
  }
  const mime = await sniffVideoMagic(file);
  if (!mime) {
    throw new Error(
      bt('chrome.media.video_not_video', "That file doesn't look like a real video."),
    );
  }
  const { width, height, duration, previewUrl, poster } = await decodeVideoMeta(file);
  return { width, height, duration, mime, previewUrl, poster };
}

/**
 * Load the file's metadata into a detached `<video>`, grab a poster frame, and hand the object URL back
 * instead of revoking it (see {@link VideoMeta.previewUrl}). The URL is returned on every path, including
 * errors, so the caller has exactly one thing to revoke no matter how the decode went.
 *
 * All of this runs against a `blob:` URL — the file is already in memory, so seeking and drawing a frame
 * costs no network at all.
 */
function decodeVideoMeta(file: File): Promise<{
  width: number;
  height: number;
  duration: number;
  previewUrl: string;
  poster?: Blob;
}> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    v.playsInline = true;

    let settled = false;
    const done = (width: number, height: number, duration: number, poster?: Blob) => {
      if (settled) return; // the timeout and a real event can race
      settled = true;
      resolve({ width, height, duration, previewUrl: url, poster });
    };

    // A codec the browser half-supports can fire loadedmetadata and then never seek. Never let that hang
    // an upload: give up on the poster and carry on with whatever dimensions we have.
    const timer = setTimeout(
      () =>
        done(v.videoWidth, v.videoHeight, Number.isFinite(v.duration) ? Math.round(v.duration) : 0),
      POSTER_TIMEOUT_MS,
    );
    const finish = (width: number, height: number, duration: number, poster?: Blob) => {
      clearTimeout(timer);
      done(width, height, duration, poster);
    };

    v.onloadedmetadata = () => {
      const width = v.videoWidth;
      const height = v.videoHeight;
      const seconds = Number.isFinite(v.duration) ? v.duration : 0;
      const duration = Math.round(seconds);

      v.onseeked = () => {
        void captureFrame(v).then((poster) => finish(width, height, duration, poster ?? undefined));
      };
      v.onerror = () => finish(width, height, duration);
      try {
        // A hair into the clip, not frame 0: phone recordings and fade-ins very often open on black.
        v.currentTime = seconds > 0 ? Math.min(0.1, seconds / 2) : 0;
      } catch {
        finish(width, height, duration);
      }
    };
    v.onerror = () => finish(0, 0, 0);
    v.src = url;
  });
}

/** Pull the first video File from a paste/drop event's items, or null. */
export function videoFromTransfer(items: DataTransferItemList | null): File | null {
  if (!items) return null;
  for (const item of Array.from(items)) {
    if (item.kind === 'file' && item.type.startsWith('video/')) {
      const f = item.getAsFile();
      if (f) return f;
    }
  }
  return null;
}
